import React, { useState } from 'react';
import { X, MessageCircle, Mail, BookOpen, CreditCard } from 'lucide-react';
import type { Consultant } from './ConsultantCard';
import { Button } from '../common/Button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ConsultantChat } from '../chat/consultant/ConsultantChat';

interface ServiceModalProps {
  consultant: Consultant;
  serviceType: 'chat' | 'email' | 'study';
  onClose: () => void;
}

export const ServiceModal: React.FC<ServiceModalProps> = ({
  consultant,
  serviceType,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const { user } = useAuth();

  const serviceInfo = {
    chat: {
      title: 'Chat por minuto',
      icon: MessageCircle,
      price: consultant.chatPrice,
      description: 'Converse em tempo real com o consultor. Você será cobrado por minuto de conversa.'
    },
    email: {
      title: 'Consulta por email',
      icon: Mail,
      price: consultant.emailPrice,
      description: 'Receba uma análise detalhada em até 48 horas.'
    },
    study: {
      title: 'Estudo completo',
      icon: BookOpen,
      price: consultant.studyPrice,
      description: 'Análise aprofundada com todos os aspectos da sua carta astral.'
    }
  }[serviceType];

  const handleStartService = async () => {
    if (!user) {
      toast.error('Por favor, faça login para continuar');
      return;
    }

    if (serviceType === 'chat') {
      setShowChat(true);
    } else {
      setLoading(true);
      try {
        // Pagamentos retornam em fase futura (server responde 501)
        toast.error('Contratação por email/estudo em breve — pagamentos migram depois');
      } finally {
        setLoading(false);
      }
    }
  };

  if (showChat) {
    return <ConsultantChat consultant={consultant} onClose={() => setShowChat(false)} />;
  }

  const Icon = serviceInfo.icon;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-indigo-950 rounded-lg p-6 w-full max-w-md relative animate-fadeIn">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-purple-200 hover:text-purple-100"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="flex items-center space-x-4 mb-6">
          <div className="p-3 bg-purple-500/20 rounded-lg">
            <Icon className="w-6 h-6 text-purple-300" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-purple-200">{serviceInfo.title}</h3>
            <p className="text-purple-200/70">com {consultant.name}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-purple-200/90 text-sm">{serviceInfo.description}</p>
          </div>

          <div className="bg-white/10 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-purple-200">Valor</span>
              <span className="text-lg font-bold text-purple-200">
                R$ {serviceInfo.price}
                {serviceType === 'chat' && '/min'}
              </span>
            </div>
          </div>

          <Button
            onClick={handleStartService}
            loading={loading}
            className="w-full"
            icon={<CreditCard className="w-5 h-5" />}
          >
            {serviceType === 'chat' ? 'Iniciar Chat' : 'Contratar serviço'}
          </Button>
        </div>
      </div>
    </div>
  );
};